// @flow

import type ExperimentsClient from 'app/common/lib/ExperimentsClient';
import { calculatePinHeight } from 'app/common/react/components/growth/unauth/pin/Pin';
import type { ContextType, PinType } from 'app/common/react/components/growth/unauth/lib/PropTypes';
import {
  getMaxColumns,
  GRID_GUTTER_WIDTH,
  GRID_ITEM_WIDTH,
  MAX_PIN_IMAGE_HEIGHT,
  MIN_COLUMNS,
} from 'app/common/lib/react/gridUtils';

const cssStyles = (
  pins: Array<PinType>,
  context: ContextType,
  seoUnauthExperiments: ExperimentsClient,
  hideMeta: boolean = false,
  numServerRenderedPins?: number,
  hideAllMeta: ?boolean,
  isPaidTraffic: boolean = false,
  pageType?: string = '',
) => {
  let styles = '';
  const margin = GRID_GUTTER_WIDTH;
  const pinImageWidth = GRID_ITEM_WIDTH;
  const minColumns = MIN_COLUMNS;
  const ssrPinCount = numServerRenderedPins || pins.length;

  // getMaxColumns use pinCnt for maxColumn, currently we fetch 125 pin for bot ssr, which generate tons of unused  css.
  // According to https://matttutt.me/profiling-googlebot-using-google-analytics-fetch-and-render/
  // Google bot use 1024 * 768  so it's safe to set arbitrary number of 5 as max column, which
  // support maxwidth from 700px to 1400px. we don't need to do this for non bot cause the save is less meaningful
  const inSEOCssOptimizationExp =
    (context.pageType === 'pin' &&
      (
        seoUnauthExperiments.v2ActivateExperiment('unauth_web_pin_grid_css_op_pin_page_v2') || ''
      ).startsWith('enabled')) ||
    context.pageType === 'board' ||
    context.pageType === 'topic';
  const ssrMaxColumn = 5;
  const pinCnt =
    inSEOCssOptimizationExp && context.isBot ? Math.min(pins.length, ssrMaxColumn) : pins.length;
  const maxColumns = getMaxColumns(pinCnt);

  const itemWidth = pinImageWidth + margin;
  let minWidth = 0;
  let maxWidth = minColumns * itemWidth + itemWidth - 1;

  // Loop through pins and record heights and store
  const pinHeights = pins.map((pin) =>
    calculatePinHeight(
      pin,
      MAX_PIN_IMAGE_HEIGHT,
      context.pageType,
      seoUnauthExperiments,
      hideMeta,
      hideAllMeta,
      isPaidTraffic,
      context.locale,
    ),
  );

  let columnStarts = [];
  let columnHeights = [];
  let minColumn;

  const pinHeightsMapCb = (pinHeight, idx) => {
    if (!(context && context.isBot) && idx > ssrPinCount) {
      return;
    }

    styles += `
      .Pin__${idx} {
        left: ${columnStarts[minColumn]}px;
        top: ${columnHeights[minColumn]}px;
      }`;
    columnHeights[minColumn] = columnHeights[minColumn] + pinHeight + margin;
    let minColumnHeight = columnHeights[minColumn];
    for (let j = 0; j < columnHeights.length; j += 1) {
      if (columnHeights[j] < minColumnHeight) {
        minColumn = j;
        minColumnHeight = columnHeights[j];
      }
    }
  };

  for (let i = minColumns; i < maxColumns + 1; i += 1) {
    styles += `@media (min-width: ${minWidth}px) `;
    if (i < maxColumns) {
      styles += `and (max-width: ${maxWidth}px) {`;
    } else {
      styles += '{';
    }
    columnStarts = [];
    columnHeights = [];
    for (let j = 0; j < i; j += 1) {
      columnStarts.push(j * itemWidth);
      columnHeights.push(0);
    }
    minColumn = 0;
    pinHeights.map(pinHeightsMapCb);

    let maxColumnHeight = columnHeights[minColumn];
    for (let j = 0; j < columnHeights.length; j += 1) {
      if (columnHeights[j] > maxColumnHeight) {
        maxColumnHeight = columnHeights[j];
      }
    }

    styles += `
    .pinGrid {
      width: ${i * itemWidth - margin}px;
      height: ${maxColumnHeight}px;
    }
    .gridCentered {
      width: ${i * itemWidth}px;
    }
    `;
    styles += '}';

    minWidth = maxWidth + 1;
    maxWidth += itemWidth;
  }

  // Sync up these padding-bottom values with AppBanner height.
  styles += `
.gridCentered {
  margin: 0 auto;
}
.itemCentered {
  margin: 0 auto;
}
.Grid__Container {
  height: 100%;
  margin: 0 auto;
  position: relative;
}

.Grid__Item {
  position: absolute;
}
  `;
  return styles;
};

export default cssStyles;
